import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, FloppyDisk, Warning, CheckCircle, MagnifyingGlass, Target,
  WarningOctagon, Info,
} from '@phosphor-icons/react';
import { useTenantPath } from '../../lib/tenantPath';
import { API, inr, fmtDate, ProgressBar } from './_shared';

import { PageSkeleton } from '../../components/Skeletons';
/**
 * Commitment picker — the CORE user-facing screen of this feature.
 *
 * Left column: available quote basket (search + tick).
 * Right column: sticky-top target progress + running total + submit CTA.
 *
 * When user under-commits and clicks Submit, backend responds 409 with an
 * `under_committed` code. We show a big blocker modal offering two paths:
 *   (1) "Pick more quotes" → dismiss, keep editing
 *   (2) "Submit anyway (send for manager approval)" → resubmit with
 *       `request_under_commit_approval: true`. Optionally include a reason.
 */
export default function CommitmentPage() {
  const { periodId } = useParams();
  const nav = useNavigate();
  const tp = useTenantPath();
  const [available, setAvailable] = useState([]);
  const [target, setTarget] = useState(0);
  const [selected, setSelected] = useState(new Set());
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [underCommitBlocker, setUnderCommitBlocker] = useState(null); // {committed, target, gap}
  const [underCommitReason, setUnderCommitReason] = useState('');

  useEffect(() => {
    axios.get(`${API}/reviews/periods/${periodId}/available-quotes`)
      .then((r) => {
        setAvailable(r.data.quotations || []);
        setTarget(r.data.my_target || 0);
        setCurrent(r.data.my_current_commitment || null);
        // Pre-select whatever the user already committed to (they may just be editing)
        const pre = new Set((r.data.my_current_commitment?.quote_ids) || []);
        // Also add anything the backend flagged as already_selected (safety belt)
        (r.data.quotations || []).forEach((q) => { if (q.already_selected) pre.add(q.id); });
        setSelected(pre);
      })
      .catch((e) => setError(e.response?.data?.detail || 'Failed to load quotations'))
      .finally(() => setLoading(false));
  }, [periodId]);

  const committedValue = useMemo(() => {
    let s = 0;
    for (const q of available) if (selected.has(q.id)) s += q.total || 0;
    return s;
  }, [selected, available]);

  const coverage = target > 0 ? (committedValue / target * 100) : 0;
  const isUnder = committedValue < target;
  const gap = Math.max(0, target - committedValue);

  const filtered = useMemo(() => {
    if (!search.trim()) return available;
    const s = search.trim().toLowerCase();
    return available.filter((q) =>
      (q.lead_company || '').toLowerCase().includes(s) ||
      (q.lead_name || '').toLowerCase().includes(s) ||
      (q.id || '').toLowerCase().includes(s)
    );
  }, [available, search]);

  const toggle = (qid) => {
    setSelected((prev) => {
      const nxt = new Set(prev);
      if (nxt.has(qid)) nxt.delete(qid); else nxt.add(qid);
      return nxt;
    });
  };

  const submit = async ({ approvalMode = false } = {}) => {
    if (selected.size === 0) { setError('Select at least one quote.'); return; }
    setSaving(true); setError('');
    try {
      await axios.post(`${API}/reviews/periods/${periodId}/commit`, {
        quote_ids: Array.from(selected),
        request_under_commit_approval: approvalMode,
        under_commit_reason: approvalMode ? underCommitReason.trim() : '',
      });
      // Refresh state — the commitment page can be reopened to edit until close
      nav(tp(`/reviews/periods/${periodId}`));
    } catch (e) {
      const detail = e.response?.data?.detail;
      // Under-commit blocker (409) — offer the modal instead of a red error
      if (e.response?.status === 409 && typeof detail === 'object' && detail?.code === 'under_committed') {
        setUnderCommitBlocker(detail);
      } else {
        setError((typeof detail === 'string') ? detail : detail?.message || 'Submit failed');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <PageSkeleton />;

  return (
    <div className="p-6 max-w-6xl mx-auto pb-32" data-testid="commitment-page">
      <button onClick={() => nav(tp('/reviews'))} className="text-sm text-[#6D8E87] hover:text-[#00a69c] inline-flex items-center gap-1 mb-4" data-testid="back-btn">
        <ArrowLeft size={14} /> Review Center
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        {/* Left: quote picker */}
        <div>
          <h1 className="app-page-title" style={{ fontFamily: 'Outfit' }}>Commit for this review</h1>
          <p className="text-sm text-[#6D8E87] mb-4">
            Pick quotations you intend to close by the review day. Only quotes visible in your basket are available.
            Once committed, the linked leads get pinned to the top of your Leads page with a &ldquo;Promised&rdquo; tag.
          </p>

          <div className="relative mb-3">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6D8E87]" />
            <input
              type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by company or lead name…"
              className="w-full pl-9 pr-3 py-2 border border-[#D5E8E4] rounded-lg text-sm focus:outline-none focus:border-[#00a69c]"
              data-testid="quote-search"
            />
          </div>

          {filtered.length === 0 ? (
            <div className="medical-card p-8 text-center text-sm text-[#6D8E87]">
              {available.length === 0
                ? 'You have no active quotations in your basket. Create quotes on the Leads page first.'
                : 'No quotes match your search.'}
            </div>
          ) : (
            <div className="space-y-2" data-testid="quote-list">
              {filtered.map((q) => {
                const on = selected.has(q.id);
                return (
                  <label
                    key={q.id}
                    className={`medical-card p-3 flex items-start gap-3 cursor-pointer transition ${
                      on ? 'border-2 border-[#00a69c] bg-[#E2F3F0]/30' : 'hover:border-[#00a69c]'
                    }`}
                    data-testid={`quote-row-${q.id}`}
                  >
                    <input
                      type="checkbox" checked={on} onChange={() => toggle(q.id)}
                      className="mt-1 w-4 h-4 accent-[#00a69c]"
                      data-testid={`quote-check-${q.id}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <span className="font-semibold text-[#052A23] text-sm">{q.lead_company}</span>
                        <span className="font-black text-[#00a69c]" style={{ fontFamily: 'Outfit' }}>{inr(q.total, { compact: false })}</span>
                      </div>
                      <div className="text-xs text-[#6D8E87] mt-0.5">
                        {q.lead_name} · <span className="uppercase text-[10px] tracking-wider">{q.status.replace(/_/g, ' ')}</span>
                        {q.created_by_name && <> · by {q.created_by_name}</>}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {/* Right: sticky target panel */}
        <aside className="lg:sticky lg:top-4 h-fit" data-testid="commit-target-panel">
          <div className="medical-card p-4 mb-3">
            <div className="text-[10px] uppercase tracking-widest text-[#92400E] font-bold flex items-center gap-1">
              <Target size={12} weight="fill" /> Your target this period
            </div>
            <div className="text-3xl font-black text-[#052A23] mt-1" style={{ fontFamily: 'Outfit' }} data-testid="target-value">
              {inr(target, { compact: false })}
            </div>

            <div className="mt-4">
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-[#6D8E87]">Committed</span>
                <span className={`font-bold ${coverage >= 100 ? 'text-emerald-600' : 'text-amber-600'}`} data-testid="committed-value">
                  {inr(committedValue, { compact: false })}
                </span>
              </div>
              <ProgressBar pct={coverage} testid="coverage-bar" />
              <div className="mt-1 flex items-center justify-between text-xs">
                <span className={`font-bold ${coverage >= 100 ? 'text-emerald-600' : 'text-amber-700'}`} data-testid="coverage-pct">
                  {coverage.toFixed(1)}%
                </span>
                <span className="text-[#6D8E87]">{selected.size} quote{selected.size === 1 ? '' : 's'}</span>
              </div>
            </div>

            {isUnder && selected.size > 0 && (
              <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded text-[11px] text-amber-800 flex items-start gap-1.5" data-testid="under-hint">
                <Warning size={12} className="shrink-0 mt-0.5" />
                <span>Shortfall: <b>{inr(gap, { compact: false })}</b>. Pick more quotes or send this for manager approval.</span>
              </div>
            )}
            {!isUnder && selected.size > 0 && (
              <div className="mt-3 p-2 bg-emerald-50 border border-emerald-200 rounded text-[11px] text-emerald-800 flex items-start gap-1.5" data-testid="over-hint">
                <CheckCircle size={12} className="shrink-0 mt-0.5" weight="fill" />
                <span>You&apos;re {(coverage - 100).toFixed(1)}% over target — nice.</span>
              </div>
            )}

            {error && <div className="mt-3 text-xs text-red-600" data-testid="submit-error">{error}</div>}

            <button
              onClick={() => submit({ approvalMode: false })}
              disabled={saving || selected.size === 0}
              className="mt-4 w-full btn-primary flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="submit-commit-btn"
            >
              <FloppyDisk size={16} weight="bold" />
              {saving ? 'Submitting…' : current ? 'Update commitment' : 'Submit commitment'}
            </button>
          </div>

          <div className="text-[10px] text-[#6D8E87] flex items-start gap-1">
            <Info size={11} className="shrink-0 mt-0.5" />
            <span>Your commitment locks these quotes from being picked in other overlapping periods until this review closes.</span>
          </div>
        </aside>
      </div>

      {/* Under-commit blocker modal */}
      {underCommitBlocker && (
        <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4" data-testid="under-commit-modal">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 text-amber-600 grid place-items-center shrink-0">
                <WarningOctagon size={22} weight="fill" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-bold text-[#052A23]" style={{ fontFamily: 'Outfit' }}>Below target</h3>
                <p className="text-sm text-[#3B5A54] mt-1">
                  You&apos;re committing <b>{inr(underCommitBlocker.committed_value, { compact: false })}</b> against a target of <b>{inr(underCommitBlocker.target_value, { compact: false })}</b> — a shortfall of <b className="text-amber-700">{inr(underCommitBlocker.gap, { compact: false })}</b>.
                </p>
                <p className="text-xs text-[#6D8E87] mt-2">
                  You can either add more quotes to hit the target, or submit anyway — but that goes to your 1+ manager for approval, and the review will always show this commitment was inadequate.
                </p>

                <textarea
                  rows={3} value={underCommitReason}
                  onChange={(e) => setUnderCommitReason(e.target.value)}
                  placeholder="Reason for under-committing (optional — visible to your manager)"
                  className="mt-3 w-full px-3 py-2 border border-[#D5E8E4] rounded-lg text-sm resize-none"
                  data-testid="under-commit-reason"
                />
              </div>
            </div>

            <div className="mt-5 flex flex-col sm:flex-row gap-2">
              <button
                onClick={() => setUnderCommitBlocker(null)}
                className="flex-1 px-4 py-2 text-sm border border-[#D5E8E4] text-[#3B5A54] rounded-lg hover:bg-[#F4F9F7]"
                data-testid="blocker-cancel-btn"
              >
                ← Pick more quotes
              </button>
              <button
                onClick={() => { setUnderCommitBlocker(null); submit({ approvalMode: true }); }}
                disabled={saving}
                className="flex-1 px-4 py-2 text-sm font-semibold bg-amber-600 hover:bg-amber-700 text-white rounded-lg disabled:opacity-50"
                data-testid="blocker-submit-anyway-btn"
              >
                Submit anyway (needs approval)
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
